import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, UserPlus, Phone, MapPin, FileText, HandCoins, ArrowRight } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { tk, bn, fmtDate } from "../lib/format";
import { Empty, Loader, Modal, Field, GlassPage, GlassHeader } from "../components/kit";
import ContactPicker from "../components/ContactPicker";
import { useDebounced } from "../components/ProductSearch";

function CustomerForm({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ name: "", mobile: "", address: "", opening_due: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/customers", { ...f, opening_due: +f.opening_due || 0 });
      toast.success("ক্রেতা যোগ হয়েছে");
      onDone();
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="all-customer-form">
      <Field label="নাম *"><input className="inp" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="all-customer-name-input" autoFocus /></Field>
      <Field label="মোবাইল">
        <div className="flex gap-2">
          <input className="inp flex-1" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="all-customer-mobile-input" />
          <ContactPicker testid="all-customer-contact-picker" onPick={(c) => setF((p) => ({ ...p, mobile: c.tel || p.mobile, name: p.name?.trim() ? p.name : (c.name || "") }))} />
        </div>
      </Field>
      <Field label="ঠিকানা"><input className="inp" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="all-customer-address-input" /></Field>
      <Field label="আগের বাকি (যদি থাকে)"><input className="inp num" type="number" min="0" step="any" value={f.opening_due} onChange={(e) => setF({ ...f, opening_due: e.target.value })} data-testid="all-customer-opening-due-input" /></Field>
      <button className="btn btn-primary w-full" disabled={busy} data-testid="all-customer-form-submit">সেভ করুন</button>
    </form>
  );
}

function initials(name: string) {
  const s = (name || "").trim();
  return s ? s.slice(0, 1) : "ক";
}

export default function AllCustomers() {
  const [q, setQ] = useState("");
  const [list, setList] = useState<any[] | null>(null);
  const [add, setAdd] = useState(false);
  const dq = useDebounced(q);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/customers", { params: { q: dq, status: "" } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [dq]);
  useEffect(() => { load(); }, [load]);

  const total = list?.length || 0;
  const dueCount = (list || []).filter((c) => c.due > 0).length;

  return (
    <GlassPage testid="all-customers-page">
      <GlassHeader
        title="সব কাস্টমার"
        subtitle="সকল ক্রেতার প্রোফাইল — নাম, মোবাইল, মোট কেনাকাটা ও বাকি"
        actions={<button className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-semibold transition-all active:scale-95 shadow-lg shadow-emerald-500/25" onClick={() => setAdd(true)} data-testid="all-customers-add-button"><UserPlus size={17} /> নতুন ক্রেতা</button>}
      />

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="glass-card p-4 md:p-5" data-testid="all-customers-total">
          <p className="text-sm font-medium text-slate-300">মোট ক্রেতা</p>
          <p className="num mt-2 text-xl md:text-2xl font-bold text-white" data-testid="all-customers-total-value">{bn(total)} জন</p>
        </div>
        <div className="glass-card p-4 md:p-5" data-testid="all-customers-due-count">
          <p className="text-sm font-medium text-slate-300">বাকি আছে</p>
          <p className="num mt-2 text-xl md:text-2xl font-bold text-rose-300" data-testid="all-customers-due-count-value">{bn(dueCount)} জন</p>
        </div>
      </div>

      <div className="glass-card p-4 mb-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="ginp pl-10" placeholder="নাম / মোবাইল / ঠিকানা দিয়ে খুঁজুন" value={q} onChange={(e) => setQ(e.target.value)} data-testid="all-customers-search-input" />
        </div>
      </div>

      {!list ? <Loader /> : list.length === 0 ? <Empty text="কোনো ক্রেতা নেই" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="all-customers-grid">
          {list.map((c) => (
            <div key={c.id} className="glass-card p-4 flex flex-col gap-3 hover:bg-white/5 transition-colors" data-testid={`all-customer-card-${c.id}`}>
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 grid place-items-center text-white font-bold text-lg shadow-md">{initials(c.name)}</span>
                <div className="min-w-0 flex-1">
                  <button className="font-bold text-white text-[15px] truncate text-left hover:text-blue-300 transition-colors" onClick={() => nav(`/customers/${c.id}`)} data-testid={`all-customer-name-${c.id}`}>{c.name}</button>
                  <p className="text-xs text-slate-400 flex items-center gap-1 num mt-0.5"><Phone size={12} /> {bn(c.mobile) || "—"}</p>
                  {c.address && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5 truncate"><MapPin size={12} /> {c.address}</p>}
                </div>
                <span className={`shrink-0 text-xs font-bold px-2 py-1 rounded-lg ${c.due > 0 ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid={`all-customer-due-badge-${c.id}`}>{c.due > 0 ? `বাকি ${tk(c.due)}` : "পরিশোধিত"}</span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="rounded-lg bg-white/5 py-2">
                  <p className="text-[11px] text-slate-400">মোট কেনাকাটা</p>
                  <p className="num text-sm font-semibold text-white">{tk(c.total_bill)}</p>
                </div>
                <div className="rounded-lg bg-white/5 py-2">
                  <p className="text-[11px] text-slate-400">মোট পরিশোধ</p>
                  <p className="num text-sm font-semibold text-emerald-300">{tk(c.total_paid)}</p>
                </div>
              </div>

              <p className="text-[11px] text-slate-400">শেষ কেনাকাটা: <span className="num">{fmtDate(c.last_memo_date)}</span></p>

              <div className="flex gap-2 mt-auto pt-1">
                <button className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 text-sm font-semibold transition-all active:scale-95" onClick={() => nav(`/customers/${c.id}`)} data-testid={`all-customer-profile-button-${c.id}`}><FileText size={14} /> প্রোফাইল <ArrowRight size={13} /></button>
                {c.due > 0 && <button className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold transition-all active:scale-95" onClick={() => nav(`/collection?customer=${c.id}`)} data-testid={`all-customer-collect-button-${c.id}`}><HandCoins size={14} /> আদায়</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={add} onClose={() => setAdd(false)} title="নতুন ক্রেতা" testid="all-customer-add-modal">
        <CustomerForm onDone={() => { setAdd(false); load(); }} />
      </Modal>
    </GlassPage>
  );
}
